import { Box, Grid, IconButton, Stack, Typography } from '@mui/material';

import React, { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';
import { Controller, useForm } from 'react-hook-form';

import { ReactComponent as DrawerClose } from 'common/assets/images/drawer-close.svg';
import AccordionComp from 'common/components/Accordion/AccordionComp';
import ButtonComp from 'common/components/Button/ButtonComp';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { DATA_ACCESS_PERMISSIONS_ACTIONS } from 'common/constants/PermissionsConstants';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { setRolePermissionDrawer } from 'common/store/reducer/permissionsReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { IUserSelectListOptions } from 'features/Settings/api/type-def';
import UsersListSelection from 'features/Settings/Components/UsersListSelection/UsersListSelection';
import { UserPayloadInterface } from 'features/Settings/Workflow/api/type-def';

import {
	getDataPermissions,
	getModulePermissions,
	createRoleData,
	updateRoleData,
} from '../api';
import {
	IRoleDrawerAccordionNames,
	ICreateRoleRequestPayload,
	IModulePermissionResponseList,
	ICreateRolePermissionsLoadingFlags,
} from '../api/type-def';

import DataAccessPermissionsModule, {
	IDataPermission,
} from './DataAccessPermissionsModule';
import PermissionsModules from './PermissionsModules';
import RoleActionPermission from './RoleActionPermission';

import styles from './CreateRole.module.scss';

const {
	CREATELABEL,
	EDITLABEL,
	CREATE,
	READ,
	UPDATE,
	DELETE,
	UPDATEALL,
	READALL,
} = DATA_ACCESS_PERMISSIONS_ACTIONS;

const { OK, BAD_REQUEST, CREATED } = HTTP_STATUS_CODES;

const CreateRole = () => {
	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);
	const rolePermissionDrawerState = useSelector(
		(state: RootState) => state.permissionsmodule.permissionRoleDrawer,
	);

	const selectedRoleDetails = useSelector(
		(state: RootState) => state.permissionsmodule.roleDetails,
	);

	const { isToastOpen, severity, message } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const defaultAddformValues = {
		roleName: selectedRoleDetails?.data?.roleName,
		roleDescription: selectedRoleDetails?.data?.roleDesc,
	};
	const emptyRolesData = {
		roleName: '',
		roleDescription: '',
	};
	const dispatch = useDispatch();

	const {
		control,
		reset,
		getValues,
		formState: { isValid, isDirty },
	} = useForm<any>({
		mode: 'onChange',
		defaultValues: defaultAddformValues || emptyRolesData,
	});

	const [formData, setFormData] = useState({
		roleName: selectedRoleDetails?.data?.roleName || '',
		roleDescription: selectedRoleDetails?.data?.roleDesc || '',
	});

	const { drawerCloseIconColor } = styles;

	const [selectedUserMailList, setSelectedUserMailList] = useState<
		IUserSelectListOptions[]
	>([]);
	const [accordionExpanded, setExpanded] = useState<IRoleDrawerAccordionNames>({
		module: false,
		dataaccess: false,
	});
	const [modulesData, setModulesData] = useState<
		IModulePermissionResponseList[]
	>([]);
	const [allAccessPermissions, setAllAcessPermissions] = useState<
		IDataPermission[]
	>([]);
	const [selectedModulesData, setSelectedModulesData] = useState<
		IModulePermissionResponseList[]
	>([]);
	const [selectedAccessPermissions, setSelectedAllAcessPermissions] = useState<
		string[]
	>([]);
	const [clearModuleBtn, setClearBtn] = useState<boolean>(false);
	const [selectModuleBtn, setSelectAllBtn] = useState<boolean>(false);
	const [clearAccessBtn, setClearAccessBtn] = useState<boolean>(false);
	const [selectAllAccessBtn, setSelectAccessAllBtn] = useState<boolean>(false);
	const [isDataLoading, setDataLoading] =
		useState<ICreateRolePermissionsLoadingFlags>({
			usersList: false,
			modulesList: false,
			dataAccessPermissionsList: false,
		});
	const [isUserValid, setIsUserValid] = useState<boolean>(
		selectedRoleDetails.type === CREATELABEL,
	);
	const [isModuleValid, setIsModuleValid] = useState<boolean>(
		selectedRoleDetails.type === CREATELABEL,
	);
	const [isDataAccessValid, setIsDataAccessValid] = useState<boolean>(
		selectedRoleDetails.type === CREATELABEL,
	);
	const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(
		selectedRoleDetails.type === CREATELABEL,
	);
	const [existingUsersData, setExistingUsersData] = useState<
		IUserSelectListOptions[]
	>([]);

	const handleInputChange = (field: any, value: any) => {
		setFormData((prevFormData: any) => ({
			...prevFormData,
			[field]: value,
		}));
	};

	const getAllModules = async () => {
		const type = 'modulesList';
		setDataLoading((previous: any) => ({ ...previous, [type]: true }));
		const moduleResponse = await getModulePermissions();
		if (moduleResponse?.statusCode === OK) {
			if (moduleResponse && moduleResponse?.responseBody) {
				const tempModuleResponse: IModulePermissionResponseList[] =
					moduleResponse?.responseBody;
				setModulesData(tempModuleResponse);
				if (selectedRoleDetails.type === EDITLABEL) {
					setSelectedModulesData([]);
					const moduleIds = selectedRoleDetails?.data?.modulePermissions || [];
					if (moduleIds?.length > 0) {
						const modules: IModulePermissionResponseList[] =
							tempModuleResponse.filter(
								(ele: IModulePermissionResponseList) =>
									moduleIds.indexOf(ele?.moduleId) !== -1,
							);
						setSelectedModulesData(modules);
					}
				}
			}
			setDataLoading((previous: any) => ({ ...previous, [type]: false }));
		} else {
			displayErrorMessage();
			setDataLoading((previous: any) => ({ ...previous, [type]: false }));
		}
	};

	const getDataPermission = async () => {
		const type = 'dataAccessPermissionsList';
		setDataLoading((previous: any) => ({ ...previous, [type]: true }));
		const permissionsResponse = await getDataPermissions();
		if (permissionsResponse && permissionsResponse?.responseBody) {
			const tempAcessPermissionsList: any = permissionsResponse?.responseBody;
			const formatedData: IDataPermission[] = buildAcessPermissionsData(
				tempAcessPermissionsList,
			);
			setSelectedAllAcessPermissions([]);
			if (selectedRoleDetails.type === EDITLABEL) {
				const permissions = selectedRoleDetails?.data?.dataPermissions || [];
				if (permissions?.length > 0) {
					setSelectedAllAcessPermissions(permissions);
				}
			}

			setAllAcessPermissions(formatedData);
			setDataLoading((previous: any) => ({ ...previous, [type]: false }));
		} else {
			displayErrorMessage();
			setDataLoading((previous: any) => ({ ...previous, [type]: false }));
		}
	};

	const buildAcessPermissionsData = (tempAcessPermissionsList: any) => {
		const result: IDataPermission[] = tempAcessPermissionsList.map(
			(entity: any, index: number) => {
				const permissions = getPermissionName(
					index,
					entity.objectType,
					entity.permissions,
				);
				return permissions;
			},
		);
		return result;
	};
	const getPermission = (per: any) => {
		let resp = {};
		const { id, permissionName } = per;
		switch (per?.permissionName) {
			case CREATE:
				resp = {
					createPermissionId: id,
					createPermissionName: permissionName,
				};
				break;
			case READ:
				resp = {
					readPermissionId: id,
					readPermissionName: permissionName,
				};
				break;
			case UPDATE:
				resp = {
					updatePermissionId: id,
					updatePermissionName: permissionName,
				};
				break;
			case DELETE:
				resp = {
					deletePermissionId: id,
					deletePermissionName: permissionName,
				};
				break;
			case UPDATEALL:
				resp = {
					updateAllPermissionId: id,
					updateAllPermissionName: permissionName,
				};
				break;
			case READALL:
				resp = {
					readAllPermissionId: id,
					readAllPermissionName: permissionName,
				};
				break;
			default:
		}
		return resp;
	};
	const getPermissionName = (
		index: number,
		objectType: string,
		permissions: any,
	) => {
		const permissionArray: any[] = [];
		permissions.forEach((per: unknown) => {
			const permission = getPermission(per);
			permissionArray.push(permission);
		});
		const permissionObject = permissionArray.reduce((accum, val) => {
			Object.assign(accum, val);
			return accum;
		}, {});
		return {
			id: index,
			objectType,
			...permissionObject,
		};
	};

	const handleAccordionChange = (event: React.SyntheticEvent, type: string) => {
		event.stopPropagation();

		let val = false;
		if (type === 'module') {
			val = accordionExpanded.module;
			setExpanded((previous: IRoleDrawerAccordionNames) => ({
				...previous,
				dataaccess: false,
			}));
		} else {
			val = accordionExpanded.dataaccess;
			setExpanded((previous: IRoleDrawerAccordionNames) => ({
				...previous,
				module: false,
			}));
		}
		setExpanded((previous: IRoleDrawerAccordionNames) => ({
			...previous,
			[type]: !val,
		}));
	};

	const onUserSelect = (data: IUserSelectListOptions[]) => {
		setSelectedUserMailList(data);
		const selectedUsers = selectedRoleDetails?.data?.users || [];
		if (selectedRoleDetails.type === CREATELABEL) {
			setIsUserValid(true);
		} else if (
			data?.length === 0 &&
			selectedUsers.length === 0 &&
			selectedRoleDetails.type === EDITLABEL
		) {
			setIsUserValid(false);
		} else if (
			!_.isEqual(selectedUsers, data) &&
			selectedRoleDetails.type === EDITLABEL
		) {
			setIsUserValid(true);
		} else {
			setIsUserValid(false);
		}
	};
	const onAcessPermissionSelect = (data: string[]) => {
		setSelectedAllAcessPermissions(data);
		setClearAccessBtn(false);
		setSelectAccessAllBtn(false);
		const accessPermissions = selectedRoleDetails?.data?.dataPermissions || [];
		if (selectedRoleDetails.type === CREATELABEL) {
			setIsDataAccessValid(true);
		} else if (
			data?.length === 0 &&
			accessPermissions?.length === 0 &&
			selectedRoleDetails.type === EDITLABEL
		) {
			setIsDataAccessValid(false);
		} else if (
			!_.isEqual(accessPermissions, data) &&
			selectedRoleDetails.type === EDITLABEL
		) {
			setIsDataAccessValid(true);
		} else {
			setIsDataAccessValid(false);
		}
	};

	const onModuleSelect = (data: IModulePermissionResponseList[]) => {
		setSelectedModulesData(data);
		setClearBtn(false);
		setSelectAllBtn(false);
		const moduleIds = data.map(
			(ele: IModulePermissionResponseList) => ele?.moduleId,
		);
		const ids = selectedRoleDetails?.data?.modulePermissions || [];
		const { type } = selectedRoleDetails;
		if (type === CREATELABEL) {
			setIsModuleValid(true);
		} else if (
			ids?.length === 0 &&
			moduleIds?.length === 0 &&
			selectedRoleDetails.type === EDITLABEL
		) {
			setIsModuleValid(false);
		} else if (ids?.length === moduleIds.length && type === EDITLABEL) {
			setIsModuleValid(false);
		} else if (!_.isEqual(ids, moduleIds) && type === EDITLABEL) {
			setIsModuleValid(true);
		} else {
			setIsModuleValid(false);
		}
	};
	const onModuleClearAll = () => (event: React.SyntheticEvent) => {
		event.stopPropagation();
		setSelectedModulesData([]);
		setClearBtn(true);
	};

	const onModuleSelectAll = () => (event: React.SyntheticEvent) => {
		event.stopPropagation();
		setSelectAllBtn(true);
	};
	const onDataAcessClearAll = () => (event: React.SyntheticEvent) => {
		event.stopPropagation();
		setClearAccessBtn(true);
	};

	const onDataAcessSelectAll = () => (event: React.SyntheticEvent) => {
		event.stopPropagation();
		setSelectAccessAllBtn(true);
	};
	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);

	const buildCreatePayload = () => {
		let moduleIds: string[] = [];
		let userIds: UserPayloadInterface[] = [];
		if (selectedModulesData && selectedModulesData.length > 0) {
			moduleIds = selectedModulesData.map(
				(ele: IModulePermissionResponseList) => ele?.moduleId,
			);
		}
		if (selectedUserMailList && selectedUserMailList.length > 0) {
			const usersList = selectedUserMailList.map((ele: any) => {
				const userObj = {} as UserPayloadInterface;
				userObj.email = ele.email;
				userObj.userName = ele.userName;
				userObj.firstName = ele.firstName;
				userObj.lastName = ele.lastName;
				return userObj;
			});
			userIds = usersList;
		}

		const paramsObj: any = {};
		if (selectedRoleDetails.type === EDITLABEL) {
			const editFormData = getValues();
			paramsObj.roleName = editFormData.roleName.trim();
			paramsObj.roleDesc = editFormData.roleDescription.trim();
		} else {
			paramsObj.roleName = formData.roleName.trim();
			paramsObj.roleDesc = formData.roleDescription.trim();
		}
		paramsObj.users = userIds;
		paramsObj.dataPermissions = selectedAccessPermissions;
		paramsObj.modulePermissions = moduleIds;
		paramsObj.loggedInUser = loggedInUser?.userId as string;

		return paramsObj;
	};

	const onCreateRole = async () => {
		const roleParams: ICreateRoleRequestPayload = buildCreatePayload();
		if (selectedRoleDetails.type === EDITLABEL) {
			const { roleId } = selectedRoleDetails.data;
			const response = await updateRoleData(roleParams, roleId);
			if (response.statusCode === OK) {
				displaySuccessMessage('Role Updated Successfully.');
			} else if (response.statusCode === BAD_REQUEST) {
				displayApiErrorMessage(response);
			} else {
				displayErrorMessage();
			}
		} else {
			const response = await createRoleData(roleParams);
			if (response.statusCode === CREATED) {
				displaySuccessMessage('Role Created Successfully.');
			} else if (response.statusCode === BAD_REQUEST) {
				displayApiErrorMessage(response);
			} else {
				displayErrorMessage();
			}
		}
	};

	const displayApiErrorMessage = (error: any) => {
		dispatch(
			setToastMessage({
				message: error?.errorBody?.errorMessage,
				severity: 'error',
				isToastOpen: true,
			}),
		);
	};
	const displaySuccessMessage = (sucessMsg: string) => {
		dispatch(
			setToastMessage({
				message: sucessMsg,
				severity: 'success',
				isToastOpen: true,
			}),
		);

		dispatch(setRolePermissionDrawer(!rolePermissionDrawerState));
	};
	const displayErrorMessage = () => {
		dispatch(
			setToastMessage({
				message: APP_GENERAL_ERROR_MSG,
				severity: 'error',
				isToastOpen: true,
			}),
		);
	};
	const drawerCloseHandler = () => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
		reset({ ...emptyRolesData });

		dispatch(setRolePermissionDrawer(!rolePermissionDrawerState));
	};

	useEffect(() => {
		if (selectedRoleDetails.type === CREATELABEL && isValid) {
			setIsSaveDisabled(false);
		} else if (selectedRoleDetails.type === EDITLABEL && !isValid) {
			setIsSaveDisabled(true);
		} else if (
			selectedRoleDetails.type === EDITLABEL &&
			(isUserValid ||
				isDataAccessValid ||
				isModuleValid ||
				(isValid && isDirty))
		) {
			setIsSaveDisabled(false);
		} else {
			setIsSaveDisabled(true);
		}
	}, [isDirty, isValid, isUserValid, isDataAccessValid, isModuleValid]);

	useEffect(() => {
		getAllModules();
		getDataPermission();
		if (selectedRoleDetails.type === EDITLABEL) {
			setExistingUsersData(selectedRoleDetails.data.users);
			setSelectedUserMailList(selectedRoleDetails.data.users);
		}
	}, []);

	return (
		<Box role="presentation" data-testid="permissions-roles-container">
			<div className={styles['rolespermissiontop-div']}>
				<div
					className={styles['rolespermissiontext-background']}
					data-testid="rolespermissiontop-div"
				>
					<Stack
						direction="row"
						justifyContent="flex-end"
						spacing={2}
						data-testid="close-button-stack"
					>
						<IconButton
							size="medium"
							onClick={drawerCloseHandler}
							data-testid="close-rolespermissionbtn"
						>
							<DrawerClose fill={drawerCloseIconColor} />
						</IconButton>
					</Stack>

					<Box className={styles['rolespermissiontext-box']}>
						<Stack direction="row" justifyContent="space-between">
							<Typography
								className={styles['rolespermissiontext-name']}
								data-testid="riskGpt-text"
							>
								{selectedRoleDetails?.type === 'Create'
									? toSentenceCase('Create Role')
									: toSentenceCase('Edit Role')}
							</Typography>
						</Stack>
					</Box>
				</div>
			</div>

			<Grid
				container
				xs={12}
				sm={12}
				md={12}
				xl={12}
				lg={12}
				className={styles['roles-main-padding']}
			>
				<Grid
					item
					xs={12}
					sm={12}
					md={12}
					xl={12}
					lg={12}
					className={styles['roles-middle-portion']}
				>
					<form className={styles['rolename-no-padding-left']}>
						<Grid container>
							<Grid item xs={12} sm={12} md={12} xl={12} lg={12}>
								<Controller
									name="roleName"
									control={control}
									rules={{
										required: {
											value: true,
											message: 'Please enter Role name',
										},
										validate: (value) => !!value.trim(),
									}}
									render={({
										field: { name, value, onChange },
										fieldState: { error },
									}) => (
										<TextFieldWithLabel
											label={toSentenceCase('Role Name')}
											name={name}
											id="task-name"
											value={value}
											inputProps={{
												'data-testid': 'role-name',
											}}
											onChange={(e) => {
												onChange(e);
												handleInputChange(name, e.target.value);
											}}
											error={!!error}
											required
											helperText={error ? error?.message : null}
											className={styles['rolename-field-width']}
											positionClass={styles['word-limit-position']}
											wordLimit={250}
										/>
									)}
								/>
							</Grid>
							<Grid
								item
								xs={12}
								sm={12}
								md={12}
								xl={12}
								lg={12}
								className={styles['roles-padding-desc']}
							>
								<Controller
									name="roleDescription"
									control={control}
									rules={{
										required: {
											value: true,
											message: 'Please enter Role Description',
										},
										validate: (value) => !!value.trim(),
									}}
									render={({
										field: { name, value, onChange },
										fieldState: { error },
									}) => (
										<TextFieldWithLabel
											label={toSentenceCase('Role Description')}
											name={name}
											id="role-desc-name"
											value={value}
											inputProps={{
												'data-testid': 'role-desc-name',
											}}
											onChange={(e) => {
												onChange(e);
												handleInputChange(name, e.target.value);
											}}
											error={!!error}
											required
											helperText={error ? error?.message : null}
											className={styles['rolename-field-width']}
											multiLine
											customClass
											wordLimit={420}
											positionClass={styles['word-limit-position']}
										/>
									)}
								/>
								<Grid item xs={12} sm={12} md={12} xl={12} lg={12}>
									<UsersListSelection
										onUserListItemSelect={onUserSelect}
										existingUsersData={existingUsersData}
										actionType={
											selectedRoleDetails?.type === EDITLABEL
												? 'edit'
												: 'create'
										}
									/>
								</Grid>
								<Grid item xs={12} sm={12} md={12} data-testid="module">
									{!isDataLoading.modulesList && (
										<AccordionComp
											expanded={accordionExpanded.module}
											onChange={(event) =>
												handleAccordionChange(event, 'module')
											}
											className={styles['roles-padding-bottom']}
											data-testid="accordian-comp-module"
											key="module"
											isLeftAccordian
											dataTestId="module-accordion"
											summary={
												<RoleActionPermission
													label={toSentenceCase('Module Permissions')}
													selectAll={onModuleSelectAll}
													clearAll={onModuleClearAll}
													displayActions={accordionExpanded.module}
												/>
											}
											details={
												<PermissionsModules
													data={modulesData}
													onModuleSelect={onModuleSelect}
													selectedModules={selectedModulesData}
													clearButton={clearModuleBtn}
													selectButton={selectModuleBtn}
												/>
											}
										/>
									)}
								</Grid>

								<Grid item xs={12} sm={12} md={12} data-testid="dataaccess-id">
									<AccordionComp
										expanded={accordionExpanded.dataaccess}
										onChange={(event) =>
											handleAccordionChange(event, 'dataaccess')
										}
										className={styles['roles-padding-top']}
										dataTestId="accordian-comp"
										key="datacesspermission"
										isLeftAccordian
										summary={
											<RoleActionPermission
												label={toSentenceCase('Data Access Permissions')}
												selectAll={onDataAcessSelectAll}
												clearAll={onDataAcessClearAll}
												displayActions={accordionExpanded.dataaccess}
											/>
										}
										details={
											<DataAccessPermissionsModule
												selectedPermissionModules={selectedAccessPermissions}
												onAcessPermissionSelect={onAcessPermissionSelect}
												data={allAccessPermissions}
												clearButton={clearAccessBtn}
												selectButton={selectAllAccessBtn}
											/>
										}
									/>
								</Grid>
							</Grid>
						</Grid>
					</form>
				</Grid>
			</Grid>
			<Grid
				item
				xs={12}
				sm={12}
				md={12}
				xl={12}
				lg={12}
				p={1}
				className={styles['btn-position']}
			>
				<Stack
					direction="row"
					className={styles['btn-rolespermissionbutton']}
					spacing={2}
				>
					<ButtonComp
						clickHandler={drawerCloseHandler}
						btnType="secondary"
						data-testid="cancel-btn"
					>
						Cancel
					</ButtonComp>
					<ButtonComp
						btnType="primary"
						clickHandler={onCreateRole}
						disabled={isSaveDisabled}
						data-testid="save-btn"
					>
						{selectedRoleDetails?.type === EDITLABEL ? 'Save' : 'Create'}
					</ButtonComp>
				</Stack>
			</Grid>

			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
				data-testid="alert-component"
			/>
		</Box>
	);
};

export default CreateRole;
